import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import * as Sentry from '@sentry/react'
import { ThemeContext } from './context/ThemeContext'
import { useTheme } from './hooks/useTheme'
import { useAnalytics } from './hooks/useAnalytics'
import { useConsent } from './hooks/useConsent'
import Header          from './components/Header'
import Footer          from './components/Footer'
import CookieConsent   from './components/CookieConsent'
import AnalyticsLoader from './components/AnalyticsLoader'
import NotFound        from './pages/NotFound'

const Home         = lazy(() => import('./pages/Home'))
const Colostrum    = lazy(() => import('./pages/Colostrum'))
const Beauty       = lazy(() => import('./pages/Beauty'))
const Uzlet        = lazy(() => import('./pages/Uzlet'))
const Rolam        = lazy(() => import('./pages/Rolam'))
const UtmGenerator = lazy(() => import('./pages/UtmGenerator'))
const VideoPage    = lazy(() => import('./pages/VideoPage'))
const Adatkezeles  = lazy(() => import('./pages/Adatkezeles'))
const Aszf         = lazy(() => import('./pages/Aszf'))
const Kapcsolat    = lazy(() => import('./pages/Kapcsolat'))
const Linktree     = lazy(() => import('./pages/Linktree'))
const LinkSzerkeszto = lazy(() => import('./pages/LinkSzerkeszto'))
const Koszonom     = lazy(() => import('./pages/Koszonom'))

function ScrollToHash() {
  const { hash, pathname } = useLocation()
  useEffect(() => {
    if (!hash) return
    const id = hash.slice(1)
    const tryScroll = () => {
      const el = document.getElementById(id)
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return true }
      return false
    }
    if (!tryScroll()) {
      const t = setTimeout(tryScroll, 80)
      return () => clearTimeout(t)
    }
  }, [hash, pathname])
  return null
}

function AppInner() {
  useAnalytics()
  const { consent, acceptAll, acceptNecessary } = useConsent()

  return (
    <div className="min-h-screen flex flex-col bg-page text-body-text">
      <ScrollToHash />
      <AnalyticsLoader consent={consent} />
      <Header />
      <main className="flex-1">
        <Suspense fallback={null}>
          <Routes>
            <Route path="/"             element={<Uzlet />} />
            <Route path="/colostrum"    element={<Colostrum />} />
            <Route path="/5in1"         element={<Beauty />} />
            <Route path="/uzlet"        element={<Uzlet />} />
            <Route path="/rolam"        element={<Rolam />} />
            <Route path="/utm"          element={<UtmGenerator />} />
            <Route path="/v"            element={<VideoPage />} />
            <Route path="/adatkezeles"    element={<Adatkezeles />} />
            <Route path="/aszf"         element={<Aszf />} />
            <Route path="/kapcsolat"      element={<Kapcsolat />} />
            <Route path="/l"              element={<Linktree />} />
            <Route path="/l/:slug"        element={<Linktree />} />
            <Route path="/linkszerkeszto" element={<LinkSzerkeszto />} />
            <Route path="/koszonom"       element={<Koszonom />} />
            <Route path="*"               element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsent
        consent={consent}
        onAcceptAll={acceptAll}
        onNecessaryOnly={acceptNecessary}
      />
    </div>
  )
}

export default function App() {
  const { theme, toggle } = useTheme()
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <ThemeContext.Provider value={{ theme, toggle }}>
        <BrowserRouter>
          <AppInner />
        </BrowserRouter>
      </ThemeContext.Provider>
    </Sentry.ErrorBoundary>
  )
}

function ErrorFallback() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-page">
      <p className="text-4xl">⚠️</p>
      <h1 className="font-display text-2xl text-heading">Váratlan hiba történt</h1>
      <p className="text-muted-text text-sm max-w-sm">
        A hiba automatikusan rögzítve lett. Kérjük, töltsd újra az oldalt.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="mt-2 px-6 py-2.5 rounded-xl text-sm font-bold text-ink"
        style={{ background: 'linear-gradient(90deg,#C9A84C,#F5EDD0)' }}
      >
        Oldal újratöltése
      </button>
    </div>
  )
}
